import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
	Autocomplete,
	Box,
	Button,
	FormControl,
	MenuItem,
	Select,
	TextField
} from "@mui/material";
import { useEffect, useState } from "react";

type TimeUnit = "minutes" | "hours" | "days";

interface TimeJumpSettings {
	amount: string;
	unit: TimeUnit;
}

interface TimeJumpControlProps {
	disabled?: boolean;
	onTimeJump: (days: number, hours?: number, minutes?: number) => Promise<void>;
}

const TIME_AMOUNTS = ["1", "7", "10", "30"];
const LOCAL_STORAGE_TIME_JUMP_SETTINGS = "debugTimeJumpSettings";
const DEFAULT_TIME_JUMP_SETTINGS: TimeJumpSettings = {
	amount: "10",
	unit: "minutes"
};

const isTimeUnit = (value: unknown): value is TimeUnit =>
	value === "minutes" || value === "hours" || value === "days";

const getSavedTimeJumpSettings = (): TimeJumpSettings => {
	try {
		const savedSettings = localStorage.getItem(
			LOCAL_STORAGE_TIME_JUMP_SETTINGS
		);

		if (!savedSettings) {
			return DEFAULT_TIME_JUMP_SETTINGS;
		}

		const parsedSettings = JSON.parse(
			savedSettings
		) as Partial<TimeJumpSettings>;
		const savedAmount = String(parsedSettings.amount ?? "");
		const numericAmount = Number(savedAmount);

		if (
			savedAmount !== "" &&
			Number.isInteger(numericAmount) &&
			numericAmount > 0 &&
			isTimeUnit(parsedSettings.unit)
		) {
			return { amount: savedAmount, unit: parsedSettings.unit };
		}
	} catch (error) {
		console.warn("Unable to load the saved time jump settings", error);
	}

	return DEFAULT_TIME_JUMP_SETTINGS;
};

const TimeJumpControl: React.FC<TimeJumpControlProps> = ({
	disabled = false,
	onTimeJump
}) => {
	const [settings, setSettings] = useState<TimeJumpSettings>(
		getSavedTimeJumpSettings
	);
	const isBusy = disabled;
	const { amount, unit } = settings;
	const numericAmount = Number(amount);
	const isAmountValid =
		amount !== "" && Number.isInteger(numericAmount) && numericAmount > 0;

	useEffect(() => {
		if (!isAmountValid) {
			return;
		}

		try {
			localStorage.setItem(
				LOCAL_STORAGE_TIME_JUMP_SETTINGS,
				JSON.stringify(settings)
			);
		} catch (error) {
			console.warn("Unable to save the time jump settings", error);
		}
	}, [isAmountValid, settings]);

	const jumpTime = async (direction: -1 | 1) => {
		if (!isAmountValid || isBusy) {
			return;
		}

		const delta = numericAmount * direction;

		switch (unit) {
			case "minutes":
				await onTimeJump(0, 0, delta);
				break;
			case "hours":
				await onTimeJump(0, delta);
				break;
			case "days":
				await onTimeJump(delta);
				break;
		}
	};

	return (
		<Box sx={{ width: "100%" }}>
			<Box
				sx={{
					display: "grid",
					gap: "0.5rem",
					gridTemplateColumns: "5.75rem 1fr",
					width: "100%"
				}}
			>
				<Autocomplete
					freeSolo
					disableClearable
					disabled={isBusy}
					filterOptions={(options) => options}
					inputValue={amount}
					openOnFocus
					options={TIME_AMOUNTS}
					onInputChange={(_event, value) => {
						if (value === "" || /^\d+$/.test(value)) {
							setSettings((currentSettings) => ({
								...currentSettings,
								amount: value
							}));
						}
					}}
					renderOption={(props, option) => (
						<li {...props}>
							<Box component="span" sx={{ textAlign: "center", width: "100%" }}>
								{option}
							</Box>
						</li>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							error={!isAmountValid}
							inputProps={{
								...params.inputProps,
								"aria-label": "Time amount",
								inputMode: "numeric"
							}}
							placeholder="Amount"
							size="small"
							sx={{ "& .MuiInputBase-input": { textAlign: "center" } }}
						/>
					)}
					selectOnFocus
				/>
				<FormControl size="small" sx={{ minWidth: "7rem" }}>
					<Select
						aria-label="Time unit"
						disabled={isBusy}
						value={unit}
						onChange={(event) =>
							setSettings((currentSettings) => ({
								...currentSettings,
								unit: event.target.value as TimeUnit
							}))
						}
					>
						<MenuItem value="minutes">Minutes</MenuItem>
						<MenuItem value="hours">Hours</MenuItem>
						<MenuItem value="days">Days</MenuItem>
					</Select>
				</FormControl>
				<Box
					sx={{
						display: "flex",
						gap: "0.5rem",
						gridColumn: "1 / -1",
						marginTop: "0.75rem"
					}}
				>
					<Button
						aria-label={`Jump back ${amount} ${unit}`}
						disabled={isBusy || !isAmountValid}
						onClick={() => jumpTime(-1)}
						startIcon={<ArrowBackIcon />}
						variant="outlined"
						sx={{ flex: 1 }}
					>
						Back
					</Button>
					<Button
						aria-label={`Jump forward ${amount} ${unit}`}
						disabled={isBusy || !isAmountValid}
						endIcon={<ArrowForwardIcon />}
						onClick={() => jumpTime(1)}
						variant="outlined"
						sx={{ flex: 1 }}
					>
						Forward
					</Button>
				</Box>
			</Box>
		</Box>
	);
};

export default TimeJumpControl;
